import React from 'react';
import { LoiComparisonFormDto } from '../../../../slices/marketplace/loi-comparison/models/loi-comparison-form.model';
import {
    DeferredVariablePaymentType,
    CorporateEquityType,
    PurchasePriceSummary,
} from '../../../../slices/marketplace/loi-form/term-form';
import { TypeOfConsideration, isHeaderOnlyConsiderationType } from '../../../../slices/marketplace/pricing/models/type-of-consideration.enum';
import { getConsiderationRow } from '../../../../slices/marketplace/loi-comparison/models/types-of-consideration-row.model';
import {
    calculateConsiderationFees,
    SfasFeeParams,
} from '../../../../slices/marketplace/loi-comparison/consideration-fees';
import { EmployeeConsiderationPayer } from '../../../../slices/marketplace/loi-form/employee-consideration-payer.enum';
import Helpers from '../../../../utils/helper';
import InfoTooltip from '../../../../components/info-tooltip/info-tooltip';

const EMPTY_CELL = '—';

export const COLUMN_TOOLTIPS: Record<string, string> = {
    'Face Values': "The total consideration as stated in the buyer's offer, unadjusted.",
    'Fair Value':
        "VetValue's estimate of the offer's present value, adjusted for future payments and/or equity. This is an analytical approximation, not a guaranteed figure.",
    'Projected Cash Flow':
        'Values are based on the inputs below and used to calculate fees on deferred payments.',
    'Fees': 'Estimate assumes all future fees are collected simultaneously. Actual fees may vary based on timing and performance outcomes.',
};

export const formatCell = (value: number | string | null | undefined): string => {
    if (value == null || value === '') {
        return EMPTY_CELL;
    }
    return String(value);
};

export const formatCurrency = (value: number | null | undefined): string => {
    if (value == null || value === 0) {
        return EMPTY_CELL;
    }
    return Helpers.currencyFormat(value, false);
};

export const formatCurrencyAllowZero = (value: number | null | undefined): string => {
    if (value == null) {
        return EMPTY_CELL;
    }
    if (value === 0) {
        return '$0';
    }
    return Helpers.currencyFormat(value, false);
};

export const formatPercent = (
    value: number | null | undefined,
    total: number | null | undefined,
): string => {
    if (value == null || value === 0 || total == null || total === 0) {
        return EMPTY_CELL;
    }
    const pct = (value / total) * 100;
    return `${pct.toFixed(1).replace(/\.0$/, '')}%`;
};

export interface RowCellValues {
    faceValue: number | null | undefined;
    fairValue: number | null | undefined;
    fairValueExplanation: string | null | undefined;
    projectedCashFlow: number | null | undefined;
    fees: number | null | undefined;
    netOfFees: number | null | undefined;
    liabilityPayoffs: number | null | undefined;
    employeePortion: number | null | undefined;
    buyerEmployeePortion: number | null | undefined;
    netToOwner: number | null | undefined;
    fieldKey: string | null | undefined;
}

export const buildEmptyRowCells = (): RowCellValues => ({
    faceValue: null,
    fairValue: null,
    fairValueExplanation: null,
    projectedCashFlow: null,
    fees: null,
    netOfFees: null,
    liabilityPayoffs: null,
    employeePortion: null,
    buyerEmployeePortion: null,
    netToOwner: null,
    fieldKey: null,
});

const getProjectedCashFlow = (
    dto: LoiComparisonFormDto,
    type: TypeOfConsideration,
): number | null => {
    const row = dto.considerationRows ? getConsiderationRow(dto, type) : undefined;
    return row?.isActive ? row.projectedCashFlow ?? null : null;
};

export const computeLiabilityPayoffs = (dto: LoiComparisonFormDto): number =>
    (dto.vendorContractPayoffs ?? []).reduce((sum, p) => sum + (p.estimatedPayOffAmount ?? 0), 0);

export const computeTotalValues = (
    summary: PurchasePriceSummary | null | undefined,
    totalFees: number | null = null,
    liabilityPayoffs: number | null = null,
): RowCellValues => {
    if (!summary) {
        return buildEmptyRowCells();
    }
    const dfpRows = summary.deferredFixedPayments?.rows ?? [];
    const dvpRows = summary.deferredVariablePayments?.rows ?? [];

    const corpRows = summary.corporateEquity?.rows ?? [];

    const cashFace = summary.cashAtCloser?.cashAtClose ?? 0;
    const dfpFace = dfpRows.reduce((s, r) => s + (r.amount ?? 0), 0);
    const dvpFace = dvpRows.reduce((s, r) => s + (r.valueAtClosing ?? 0), 0);
    const jvFace = summary.jvEquity?.row?.valueAtClosing ?? 0;
    const corpFace = corpRows.reduce((s, r) => s + (r.valueAtClosing ?? 0), 0);
    const otherFace = summary.other ?? 0;

    const dvpFair = dvpRows.reduce((s, r) => s + (r.fairValue ?? 0), 0);
    const dvpEmployee = dvpRows.reduce((s, r) => s + (r.considerationForEmployees ?? 0), 0);
    const dvpBuyerEmployee = dvpRows.reduce((s, r) => s + (r.buyerConsiderationForEmployees ?? 0), 0);

    const corpFair = corpRows.reduce((s, r) => s + (r.fairValue ?? 0), 0);
    const corpEmployee = corpRows.reduce((s, r) => s + (r.considerationForEmployees ?? 0), 0);
    const corpBuyerEmployee = corpRows.reduce((s, r) => s + (r.buyerConsiderationForEmployees ?? 0), 0);

    const totalFace = cashFace + dfpFace + dvpFace + jvFace + corpFace + otherFace;
    const totalFairValue =
        (summary.cashAtCloser?.fairValue ?? 0) +
        (dfpRows[0]?.fairValue ?? 0) +
        dvpFair +
        (summary.jvEquity?.row?.fairValue ?? 0) +
        corpFair +
        (summary.otherFairValue ?? 0);
    const totalEmployee =
        (summary.cashAtCloser?.considerationForEmployees ?? 0) +
        (dfpRows[0]?.considerationForEmployees ?? 0) +
        dvpEmployee +
        (summary.jvEquity?.row?.considerationForEmployees ?? 0) +
        corpEmployee +
        (summary.otherConsiderationForEmployees ?? 0);
    const totalBuyerEmployee =
        (summary.cashAtCloser?.buyerConsiderationForEmployees ?? 0) +
        (dfpRows[0]?.buyerConsiderationForEmployees ?? 0) +
        dvpBuyerEmployee +
        (summary.jvEquity?.row?.buyerConsiderationForEmployees ?? 0) +
        corpBuyerEmployee +
        (summary.otherBuyerConsiderationForEmployees ?? 0);
    const baseValue = totalFairValue || totalFace || 0;
    const totalNetOfFees =
        baseValue === 0 && totalFees == null
            ? null
            : baseValue - (totalFees ?? 0);

    return {
        faceValue: totalFace || null,
        fairValue: totalFairValue || null,
        fairValueExplanation: null,
        projectedCashFlow: null,
        fees: totalFees,
        netOfFees: totalNetOfFees,
        liabilityPayoffs,
        employeePortion: totalEmployee || null,
        buyerEmployeePortion: totalBuyerEmployee || null,
        netToOwner: null,
        fieldKey: null,
    };
};

export const getFieldComment = (
    dto: LoiComparisonFormDto,
    fieldKey: string | null | undefined,
): string | undefined => {
    if (!fieldKey) return undefined;
    const fieldComments = dto.form?.termForm?.fieldComments ?? dto.form?.fieldComments ?? [];
    return fieldComments.find((c) => c.fieldKey === fieldKey)?.buyerComment;
};

export interface PriceRowProps {
    label: string;
    indented?: boolean;
    cells: RowCellValues;
    totalFaceValue: number;
    totalFairValue: number;
    showFairValue: boolean;
    showFaceColumn: boolean;
    showFairColumn: boolean;
    isBold?: boolean;
    buyerId: number;
    comment?: string;
    isDeepDive?: boolean;
    isClickable?: boolean;
    isExpanded?: boolean;
    onCellsClick?: (buyerId: number) => void;
    tooltipIdSuffix?: string;
    showLiabilityPayoffs?: boolean;
    showSellerEmployeeColumn?: boolean;
    showBuyerEmployeeColumn?: boolean;
    renderLabelCell?: boolean;
    isHeaderOnly?: boolean;
}

export const PriceRow = ({
    label,
    indented,
    cells,
    totalFaceValue,
    totalFairValue,
    showFairValue,
    showFaceColumn,
    showFairColumn,
    isBold,
    buyerId,
    comment,
    isDeepDive,
    isClickable,
    isExpanded,
    onCellsClick,
    tooltipIdSuffix = '',
    showLiabilityPayoffs,
    showSellerEmployeeColumn = true,
    showBuyerEmployeeColumn = true,
    renderLabelCell,
    isHeaderOnly,
}: PriceRowProps) => {
    const hasFairValueTooltip = !isHeaderOnly && !!cells.fairValueExplanation;
    const hasCommentTooltip = !isHeaderOnly && !!comment;

    const handleClick = !isHeaderOnly && isDeepDive && isClickable && onCellsClick
        ? () => onCellsClick(buyerId)
        : undefined;

    const rowClassName = [
        isBold ? 'total-row' : undefined,
        isHeaderOnly ? 'price-row--header-label' : undefined,
        !isHeaderOnly && isDeepDive && isClickable ? 'price-row--clickable' : undefined,
        !isHeaderOnly && isDeepDive && isExpanded ? 'price-row--expanded-cells' : undefined,
    ].filter(Boolean).join(' ') || undefined;

    const money = (value: number | null | undefined) => (isHeaderOnly ? '' : formatCurrency(value));
    const moneyAllowZero = (value: number | null | undefined) => (isHeaderOnly ? '' : formatCurrencyAllowZero(value));
    const percent = (value: number | null | undefined, total: number) => (isHeaderOnly ? '' : formatPercent(value, total));

    return (
        <tr className={rowClassName} onClick={handleClick}>
            {renderLabelCell && (
                <td className={`col-label${indented ? ' col-label--indented' : ''}`}>{label}</td>
            )}
            <td className="cell-value">
                <span>{money(cells.faceValue)}</span>
                {hasCommentTooltip && (
                    <InfoTooltip
                        text={comment}
                        idText={`comment-${buyerId}-${cells.fieldKey ?? label}${tooltipIdSuffix}`}
                    />
                )}
            </td>
            {showFaceColumn ? (
                <td className="cell-value">{percent(cells.faceValue, totalFaceValue)}</td>
            ) : (
                <td className="percent-expander-col" />
            )}
            {showFairValue && (
                <>
                    <td className="cell-value">
                        <span>{money(cells.fairValue)}</span>
                        {hasFairValueTooltip && (
                            <InfoTooltip
                                text={cells.fairValueExplanation!}
                                idText={`fair-value-${buyerId}-${cells.fieldKey ?? label}${tooltipIdSuffix}`}
                            />
                        )}
                    </td>
                    {showFairColumn ? (
                        <td className="cell-value">{percent(cells.fairValue, totalFairValue)}</td>
                    ) : (
                        <td className="percent-expander-col" />
                    )}
                </>
            )}
            <td className="cell-value">{money(cells.projectedCashFlow)}</td>
            <td className="cell-value">{moneyAllowZero(cells.fees)}</td>
            <td className="cell-value">{moneyAllowZero(cells.netOfFees)}</td>
            {showLiabilityPayoffs && <td className="cell-value">{money(cells.liabilityPayoffs)}</td>}
            {showSellerEmployeeColumn && <td className="cell-value">{money(cells.employeePortion)}</td>}
            {showBuyerEmployeeColumn && <td className="cell-value">{money(cells.buyerEmployeePortion)}</td>}
            <td className="cell-value">{money(cells.netToOwner)}</td>
        </tr>
    );
};

const computeNetOfFees = (
    fairValue: number | null | undefined,
    faceValue: number | null | undefined,
    fees: number | null | undefined,
): number | null => {
    const value = fairValue ?? faceValue ?? null;
    return value == null && fees == null ? null : (value ?? 0) - (fees ?? 0);
};

const computeNetToOwner = (
    netOfFees: number | null | undefined,
    liabilityPayoffs: number | null | undefined,
    employeePortion: number | null | undefined,
): number | null => {
    if (netOfFees == null) {
        return null;
    }
    return netOfFees - (liabilityPayoffs ?? 0) - (employeePortion ?? 0);
};

export const buildCashAtCloseRow = (dto: LoiComparisonFormDto): RowCellValues => {
    const cash = dto.form?.termForm?.purchasePriceSummary?.cashAtCloser;
    if (!cash) {
        return { ...buildEmptyRowCells(), liabilityPayoffs: computeLiabilityPayoffs(dto) };
    }
    return {
        faceValue: cash.cashAtClose,
        fairValue: cash.fairValue,
        fairValueExplanation: cash.fairValueExplanation,
        projectedCashFlow: cash.fairValue ?? cash.cashAtClose ?? null,
        fees: null,
        netOfFees: computeNetOfFees(cash.fairValue, cash.cashAtClose, null),
        liabilityPayoffs: computeLiabilityPayoffs(dto),
        employeePortion: cash.considerationForEmployees,
        buyerEmployeePortion: cash.buyerConsiderationForEmployees,
        netToOwner: null,
        fieldKey: 'cashAtClose',
    };
};

export const buildDeferredFixedRow = (dto: LoiComparisonFormDto): RowCellValues => {
    const rows = dto.form?.termForm?.purchasePriceSummary?.deferredFixedPayments?.rows ?? [];
    const projectedCashFlow = getProjectedCashFlow(dto, TypeOfConsideration.DeferredFixedPayments);
    const first = rows[0];
    if (!first) {
        return { ...buildEmptyRowCells(), projectedCashFlow };
    }
    const faceTotal = rows.reduce((s, r) => s + (r.amount ?? 0), 0);
    return {
        faceValue: faceTotal,
        fairValue: first.fairValue,
        fairValueExplanation: first.fairValueExplanation,
        projectedCashFlow,
        fees: null,
        netOfFees: computeNetOfFees(first.fairValue, faceTotal, null),
        liabilityPayoffs: null,
        employeePortion: first.considerationForEmployees,
        buyerEmployeePortion: first.buyerConsiderationForEmployees,
        netToOwner: null,
        fieldKey: 'deferredFixedPaymentsTotal',
    };
};

export const buildDeferredVariableRow = (): RowCellValues => buildEmptyRowCells();

export const buildEarnoutRow = (dto: LoiComparisonFormDto): RowCellValues => {
    const rows = dto.form?.termForm?.purchasePriceSummary?.deferredVariablePayments?.rows ?? [];
    const earnoutRows = rows.filter((r) => r.type === DeferredVariablePaymentType.Earnout);
    const projectedCashFlow = getProjectedCashFlow(dto, TypeOfConsideration.Earnout);
    const first = earnoutRows[0];
    if (!first) {
        return { ...buildEmptyRowCells(), projectedCashFlow };
    }
    const faceTotal = earnoutRows.reduce((s, r) => s + (r.valueAtClosing ?? 0), 0);
    return {
        faceValue: faceTotal,
        fairValue: first.fairValue,
        fairValueExplanation: first.fairValueExplanation,
        projectedCashFlow,
        fees: null,
        netOfFees: computeNetOfFees(first.fairValue, faceTotal, null),
        liabilityPayoffs: null,
        employeePortion: first.considerationForEmployees,
        buyerEmployeePortion: first.buyerConsiderationForEmployees,
        netToOwner: null,
        fieldKey: 'earnout',
    };
};

export const buildSyntheticJvRow = (dto: LoiComparisonFormDto): RowCellValues => {
    const rows = dto.form?.termForm?.purchasePriceSummary?.deferredVariablePayments?.rows ?? [];
    const syntheticRows = rows.filter((r) => r.type === DeferredVariablePaymentType.Synthetic);
    const projectedCashFlow = getProjectedCashFlow(dto, TypeOfConsideration.SyntheticJvEquity);
    const first = syntheticRows[0];
    if (!first) {
        return { ...buildEmptyRowCells(), projectedCashFlow };
    }
    const faceTotal = syntheticRows.reduce((s, r) => s + (r.valueAtClosing ?? 0), 0);
    return {
        faceValue: faceTotal,
        fairValue: first.fairValue,
        fairValueExplanation: first.fairValueExplanation,
        projectedCashFlow,
        fees: null,
        netOfFees: computeNetOfFees(first.fairValue, faceTotal, null),
        liabilityPayoffs: null,
        employeePortion: first.considerationForEmployees,
        buyerEmployeePortion: first.buyerConsiderationForEmployees,
        netToOwner: null,
        fieldKey: 'syntheticJvEquity',
    };
};

export const buildJvEquityRow = (dto: LoiComparisonFormDto): RowCellValues => {
    const row = dto.form?.termForm?.purchasePriceSummary?.jvEquity?.row;
    const projectedCashFlow = getProjectedCashFlow(dto, TypeOfConsideration.JvEquity);
    if (!row) {
        return { ...buildEmptyRowCells(), projectedCashFlow };
    }
    return {
        faceValue: row.valueAtClosing,
        fairValue: row.fairValue,
        fairValueExplanation: row.fairValueExplanation,
        projectedCashFlow,
        fees: null,
        netOfFees: computeNetOfFees(row.fairValue, row.valueAtClosing, null),
        liabilityPayoffs: null,
        employeePortion: row.considerationForEmployees,
        buyerEmployeePortion: row.buyerConsiderationForEmployees,
        netToOwner: null,
        fieldKey: 'jvEquityTotal',
    };
};

export const buildCorporateEquityRow = (): RowCellValues => buildEmptyRowCells();

export const buildCorporateEquityPreferredRow = (dto: LoiComparisonFormDto): RowCellValues => {
    const rows = dto.form?.termForm?.purchasePriceSummary?.corporateEquity?.rows ?? [];
    const row = rows.find((r) => r.type === CorporateEquityType.Preferred);
    const projectedCashFlow = getProjectedCashFlow(dto, TypeOfConsideration.PreferredEquity);
    if (!row) {
        return { ...buildEmptyRowCells(), projectedCashFlow };
    }
    return {
        faceValue: row.valueAtClosing,
        fairValue: row.fairValue,
        fairValueExplanation: row.fairValueExplanation,
        projectedCashFlow,
        fees: null,
        netOfFees: computeNetOfFees(row.fairValue, row.valueAtClosing, null),
        liabilityPayoffs: null,
        employeePortion: row.considerationForEmployees,
        buyerEmployeePortion: row.buyerConsiderationForEmployees,
        netToOwner: null,
        fieldKey: 'corporateEquityPreferred',
    };
};

export const buildCorporateEquityCommonRow = (dto: LoiComparisonFormDto): RowCellValues => {
    const rows = dto.form?.termForm?.purchasePriceSummary?.corporateEquity?.rows ?? [];
    const row = rows.find((r) => r.type === CorporateEquityType.Common);
    const projectedCashFlow = getProjectedCashFlow(dto, TypeOfConsideration.CommonEquity);
    if (!row) {
        return { ...buildEmptyRowCells(), projectedCashFlow };
    }
    return {
        faceValue: row.valueAtClosing,
        fairValue: row.fairValue,
        fairValueExplanation: row.fairValueExplanation,
        projectedCashFlow,
        fees: null,
        netOfFees: computeNetOfFees(row.fairValue, row.valueAtClosing, null),
        liabilityPayoffs: null,
        employeePortion: row.considerationForEmployees,
        buyerEmployeePortion: row.buyerConsiderationForEmployees,
        netToOwner: null,
        fieldKey: 'corporateEquityCommon',
    };
};

export const buildOtherRow = (dto: LoiComparisonFormDto): RowCellValues => {
    const summary = dto.form?.termForm?.purchasePriceSummary;
    const projectedCashFlow = getProjectedCashFlow(dto, TypeOfConsideration.Other);
    if (!summary) {
        return { ...buildEmptyRowCells(), projectedCashFlow };
    }
    return {
        faceValue: summary.other,
        fairValue: summary.otherFairValue,
        fairValueExplanation: summary.otherFairValueExplanation,
        projectedCashFlow,
        fees: null,
        netOfFees: computeNetOfFees(summary.otherFairValue, summary.other, null),
        liabilityPayoffs: null,
        employeePortion: summary.otherConsiderationForEmployees,
        buyerEmployeePortion: summary.otherBuyerConsiderationForEmployees,
        netToOwner: null,
        fieldKey: 'other',
    };
};

export interface PriceRowConfig {
    label: string;
    type: TypeOfConsideration;
    indented?: boolean;
}

export const PRICE_ROWS: PriceRowConfig[] = [
    { label: 'Cash at Close', type: TypeOfConsideration.CashAtClose },
    { label: 'Deferred Fixed Payments', type: TypeOfConsideration.DeferredFixedPayments },
    { label: 'Deferred Variable Payments', type: TypeOfConsideration.DeferredVariablePayments },
    { label: 'Earnout', type: TypeOfConsideration.Earnout, indented: true },
    { label: 'Synthetic JV Equity', type: TypeOfConsideration.SyntheticJvEquity, indented: true },
    { label: 'JV Equity', type: TypeOfConsideration.JvEquity },
    { label: 'Corporate Equity', type: TypeOfConsideration.CorporateEquity },
    { label: 'Common Corp Equity', type: TypeOfConsideration.CommonEquity, indented: true },
    { label: 'Preferred Corp Equity', type: TypeOfConsideration.PreferredEquity, indented: true },
    { label: 'Other', type: TypeOfConsideration.Other },
];

const ROW_BUILDERS: Record<TypeOfConsideration, (dto: LoiComparisonFormDto) => RowCellValues> = {
    [TypeOfConsideration.CashAtClose]: buildCashAtCloseRow,
    [TypeOfConsideration.DeferredFixedPayments]: buildDeferredFixedRow,
    [TypeOfConsideration.DeferredVariablePayments]: buildDeferredVariableRow,
    [TypeOfConsideration.Earnout]: buildEarnoutRow,
    [TypeOfConsideration.SyntheticJvEquity]: buildSyntheticJvRow,
    [TypeOfConsideration.JvEquity]: buildJvEquityRow,
    [TypeOfConsideration.CorporateEquity]: buildCorporateEquityRow,
    [TypeOfConsideration.PreferredEquity]: buildCorporateEquityPreferredRow,
    [TypeOfConsideration.CommonEquity]: buildCorporateEquityCommonRow,
    [TypeOfConsideration.Other]: buildOtherRow,
};

export interface ConsiderationSummary {
    rowsByType: Partial<Record<TypeOfConsideration, RowCellValues>>;
    totalCells: RowCellValues;
    portionPaidAtClose: number | null;
    projectedTransactionValue: number | null;
    projectedTransactionFee: number | null;
}

export const buildConsiderationSummary = (
    dto: LoiComparisonFormDto,
    levelOfService: number,
    sfasParams: SfasFeeParams | null = null,
): ConsiderationSummary => {
    const rowsByType: Partial<Record<TypeOfConsideration, RowCellValues>> = {};
    PRICE_ROWS.forEach(({ type }) => {
        rowsByType[type] = ROW_BUILDERS[type](dto);
    });

    const feeInputs = PRICE_ROWS
        .filter(({ type }) => !isHeaderOnlyConsiderationType(type))
        .map(({ type }) => {
            const cells = rowsByType[type]!;
            return {
                type,
                faceValue: cells.faceValue,
                fairValue: cells.fairValue,
                projectedCashFlow: cells.projectedCashFlow,
            };
        });

    const {
        feesByType,
        totalFees,
        portionPaidAtClose,
        projectedTransactionValue,
        projectedTransactionFee,
    } = calculateConsiderationFees(
        feeInputs,
        dto.considerationRows ?? [],
        dto.vvConnectFees,
        levelOfService,
        sfasParams,
    );

    let totalProjectedCashFlow: number | null = null;
    let totalNetToOwner: number | null = null;
    PRICE_ROWS.forEach(({ type }) => {
        if (isHeaderOnlyConsiderationType(type)) {
            return;
        }
        const cells = rowsByType[type]!;
        cells.fees = feesByType[type] ?? null;
        cells.netOfFees = computeNetOfFees(cells.fairValue, cells.faceValue, cells.fees);
        cells.netToOwner = computeNetToOwner(cells.netOfFees, cells.liabilityPayoffs, cells.employeePortion);
        if (cells.projectedCashFlow != null) {
            totalProjectedCashFlow = (totalProjectedCashFlow ?? 0) + cells.projectedCashFlow;
        }
        if (cells.netToOwner != null) {
            totalNetToOwner = (totalNetToOwner ?? 0) + cells.netToOwner;
        }
    });

    const totalCells = computeTotalValues(
        dto.form?.termForm?.purchasePriceSummary,
        totalFees,
        computeLiabilityPayoffs(dto),
    );
    totalCells.projectedCashFlow = totalProjectedCashFlow;
    totalCells.netToOwner = totalNetToOwner;
    return {
        rowsByType,
        totalCells,
        portionPaidAtClose,
        projectedTransactionValue,
        projectedTransactionFee,
    };
};

export interface EmployeeColumnVisibility {
    showSellerEmployeeColumn: boolean;
    showBuyerEmployeeColumn: boolean;
}

const hasEmployeeValue = (
    cells: RowCellValues[],
    key: 'employeePortion' | 'buyerEmployeePortion',
): boolean => cells.some((cell) => cell[key] != null);

export const getEmployeeColumnVisibility = (item: LoiComparisonFormDto): EmployeeColumnVisibility => {
    const rowCells = PRICE_ROWS.map(({ type }) => ROW_BUILDERS[type](item));
    const totalCells = computeTotalValues(item.form?.termForm?.purchasePriceSummary);
    const allCells = [...rowCells, totalCells];

    const payer = item.form?.termForm?.employeeConsiderationPayer;
    const sellerPays = payer === EmployeeConsiderationPayer.PaidBySeller
        || payer === EmployeeConsiderationPayer.PaidByBoth;
    const buyerPays = payer === EmployeeConsiderationPayer.PaidByBuyer
        || payer === EmployeeConsiderationPayer.PaidByBoth;

    return {
        showSellerEmployeeColumn: sellerPays && hasEmployeeValue(allCells, 'employeePortion'),
        showBuyerEmployeeColumn: buyerPays && hasEmployeeValue(allCells, 'buyerEmployeePortion'),
    };
};

export interface SectionHeaderRowProps {
    label: string;
    colSpan: number;
}

export const SectionHeaderRow = ({ label, colSpan }: SectionHeaderRowProps) => {
    return (
        <tr className="section-header-row">
            <td colSpan={colSpan}>{label}</td>
        </tr>
    );
};
